import { Util } from '@microsoft/applicationinsights-common';
import { ErrorHandler, Injectable } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { Subject } from 'rxjs';
import { debounceTime, filter, scan, skip, take, takeUntil } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { HttpActivityService } from './http-activity.service';

@Injectable({
    providedIn: 'root'
})
export class MyMonitoringService {
    appInsights: ApplicationInsights;
    private currentRoute = '/';
    private currentOperationId: string;

    constructor(private router: Router, private httpActivity: HttpActivityService) {
        this.currentOperationId = Util.generateW3CId();
        this.appInsights = new ApplicationInsights({
            config: {
                instrumentationKey: environment.appInsights.instrumentationKey
            }
        });
        this.appInsights.loadAppInsights();

        /* Every telemetry event (page view, API call, exception) passes through here before being sent to Azure. 
        We add current route name and a unique ID to all events so they all land in the right bucket in app-insights portal. */
        this.appInsights.addTelemetryInitializer((item) => {
            item.tags = item.tags || [] as any;
            item.tags['ai.operation.name'] = this.currentRoute;
            item.tags['ai.operation.id'] = this.currentOperationId;
        });

        this.trackRouteNavigation();
    }

    private trackRouteNavigation(): void {
        const cancel$ = new Subject<void>();
        let fallbackTimer: ReturnType<typeof setTimeout> | null = null;

        this.router.events.subscribe(event => {
            if (event instanceof NavigationStart) {
                /* If started navigating away or cancel any in-progress measurement, for the previous page so we don't record a half-finished load time. */
                cancel$.next();
                if (fallbackTimer !== null) {
                    clearTimeout(fallbackTimer);
                    fallbackTimer = null;
                }
            } else if (event instanceof NavigationEnd) {
                const fullUrl = event.urlAfterRedirects;
                /* Strip query params so `/route/subRoute?id=123&tab=x` groups under main route `/route/subRoute` */
                const routeName = fullUrl.split('?')[0];

                /* Update the current route context and start the page view timer. */
                this.currentRoute = routeName;
                this.currentOperationId = Util.generateW3CId();
                this.appInsights.startTrackPage(routeName);

                let alreadyStopped = false;
                const stopOnce = () => {
                    if (!alreadyStopped) {
                        alreadyStopped = true;
                        this.appInsights.stopTrackPage(routeName, fullUrl);
                    }
                };

                /* if the page makes no API calls at all, the main watcher below never fires. So after 150ms we still record a valid (fast) load time. */
                fallbackTimer = setTimeout(() => {
                    fallbackTimer = null;
                    if (this.httpActivity.activeRequests$.value === 0) {
                        stopOnce();
                    }
                }, 150);

                /* Main time calculation logic: 
                   Watch the active HTTP request count. Once the page has made at least one API call and all of them finish, wait a further 500ms of silence before stopping the clock.
                */
                this.httpActivity.activeRequests$.pipe(
                    skip(1),
                    scan(
                        (acc, curr) => ({ everBusy: acc.everBusy || curr > 0, curr }),
                        { everBusy: false, curr: 0 }
                    ),
                    filter(state => state.everBusy && state.curr === 0),
                    debounceTime(500),
                    take(1),
                    takeUntil(cancel$)
                ).subscribe(() => {
                    if (fallbackTimer !== null) {
                        clearTimeout(fallbackTimer);
                        fallbackTimer = null;
                    }
                    stopOnce();
                });
            }
        });
    }

    logPageView(name?: string, url?: string) {
        this.appInsights.trackPageView({
            name: name,
            uri: url
        });
    }

    logEvent(name: string, properties?: { [key: string]: any }) {
        this.appInsights.trackEvent({ name: name }, properties);
    }

    logMetric(name: string, average: number, properties?: { [key: string]: any }) {
        this.appInsights.trackMetric({ name: name, average: average }, properties);
    }

    logException(exception: Error, severityLevel?: number) {
        this.appInsights.trackException({ exception: exception, severityLevel: severityLevel });
    }

    logTrace(message: string, properties?: { [key: string]: any }) {
        this.appInsights.trackTrace({ message: message }, properties);
    }
}


@Injectable()
export class ErrorHandlerService extends ErrorHandler {

    constructor(private myMonitoringService: MyMonitoringService, private loggingService: MyMonitoringService) {
        super();
    }

    handleError(error: Error) {
        this.loggingService.logException(error); // Manually log exception
    }
}